import * as React from 'react'
import WorkflowList from 'airship/widgets/new-push-popover/workflow-list'
import JourneyTemplateList from './journey-template-list'
import classnames = require('classnames')
import MessageType from './message-type'
import Modal from 'airship/patterns/modal'
import SvgSprite from 'airship/patterns/svg-sprite'

import * as waffle from 'airship/modules/waffle'
import WaffleFlag from 'airship/modules/waffle/flags'

import className from 'classnames'
import Badge from 'airship/patterns/badge'

const BODY_CLASS = 'modal-open'

interface Props {
  hasPComposerAccess?: boolean
  hasExperimentsAccess?: boolean
  hasAppleNews?: boolean
  hasJourneys?: boolean
  hasTriggersTag?: boolean
  hasInAppAutomation?: boolean
  hasTemplates?: boolean
  hasScene?: boolean
  hasSurvey?: boolean
  createMessageUrl: string
  createExperimentUrl: string
  createAppleNewsUrl: string
  createAutomatedUrl: string
  createZoltarUrl: string
  createSceneUrl: string
  createAppSurveyUrl: string
  createAppExperienceUrl: string
  createTemplatesUrl: string
  createJourneyModalOpenUrl: string
}

enum TabsNames {
  SEND,
  AUTOMATION,
  OPTIMIZATION,
  CONTENT,
}

const NewPushPopover: React.FC<Props> = (props) => {
  const [open, setOpen] = React.useState(false)
  const [selectedTab, setSelectTab] = React.useState(TabsNames.SEND)

  const handleSelectTab = React.useCallback(
    (tabName) => () => {
      setSelectTab(tabName)
    },
    []
  )

  React.useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (document.body.classList) {
      document.body.classList.toggle(BODY_CLASS, open)
    }

    return () => {
      document.body.classList.remove(BODY_CLASS)
    }
  }, [open])

  return waffle.flag_is_active(WaffleFlag.GlobalCreateReworkFlag) ? (
    <>
      {props.hasPComposerAccess && <CreateNewButton />}
      <Modal size="md" isOpen={open} onClose={toggle}>
        {props.hasPComposerAccess && (
          <React.Fragment>
            <div className="create-modal__grid">
              <ul className="create-modal__menu" role="tablist">
                <li>
                  <button
                    type="button"
                    className={className({
                      active: selectedTab === TabsNames.SEND,
                    })}
                    onClick={handleSelectTab(TabsNames.SEND)}
                    role="tab"
                  >
                    Message
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    className={className({
                      active: selectedTab === TabsNames.AUTOMATION,
                    })}
                    onClick={handleSelectTab(TabsNames.AUTOMATION)}
                  >
                    Automation
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    className={className({
                      active: selectedTab === TabsNames.OPTIMIZATION,
                    })}
                    onClick={handleSelectTab(TabsNames.OPTIMIZATION)}
                  >
                    Optimization
                  </button>
                </li>
                {props.hasTemplates && (
                  <li>
                    <button
                      type="button"
                      className={className({
                        active: selectedTab === TabsNames.CONTENT,
                      })}
                      onClick={handleSelectTab(TabsNames.CONTENT)}
                    >
                      Content
                    </button>
                  </li>
                )}
              </ul>

              <div className="u-pad-0">
                {selectedTab === TabsNames.SEND && (
                  <>
                    <SendAndSchedulesMessageTypes />
                    <WorkflowList />
                  </>
                )}
                {selectedTab === TabsNames.AUTOMATION && (
                  <AutomationMessageTypes />
                )}
                {selectedTab === TabsNames.OPTIMIZATION && (
                  <OptimizationMessageTypes />
                )}
                {selectedTab === TabsNames.CONTENT && <ContentMessageTypes />}
              </div>
            </div>
          </React.Fragment>
        )}
      </Modal>
    </>
  ) : (
    <>
      {props.hasPComposerAccess && <CreateNewButton />}
      <Modal isOpen={open} onClose={toggle}>
        {props.hasPComposerAccess && (
          <React.Fragment>
            <StandardMessageTypes />
            {props.hasJourneys && (
              <JourneyTemplateList title="Sequence Templates" />
            )}
            <WorkflowList />
          </React.Fragment>
        )}
      </Modal>
    </>
  )

  function toggle(): void {
    setOpen(!open)
  }

  function SendAndSchedulesMessageTypes(): JSX.Element {
    return (
      <>
        <MessageType
          href={props.createMessageUrl}
          orientation="horizontal"
          title="Message"
          icon="composer-message"
          description="Send or schedule a message."
          data-selenium-target="composer-create-message"
        />

        {props.hasAppleNews && (
          <MessageType
            href={props.createAppleNewsUrl}
            orientation="horizontal"
            title="Apple News"
            icon="composer-applenews"
            description="Send a push notification that links to an Apple News story."
          />
        )}
      </>
    )
  }

  function AutomationMessageTypes(): JSX.Element {
    return (
      <>
        {props.hasJourneys && (
          <MessageType
            href={props.createJourneyModalOpenUrl}
            orientation="horizontal"
            title="Sequence"
            icon="composer-series"
            description="Automatically send a series of messages to users who meet certain conditions."
          />
        )}
        {props.hasTriggersTag && (
          <MessageType
            href={props.createAutomatedUrl}
            orientation="horizontal"
            title="Automation"
            icon="composer-automation"
            description="Automatically send a message to users who meet certain conditions."
          />
        )}
        {props.hasInAppAutomation && (
          <MessageType
            href={props.createZoltarUrl}
            orientation="horizontal"
            title="In-App Automation"
            icon="composer-inapp-automation"
            description="Create a message that appears in your app when users meet certain conditions."
          />
        )}
        {props.hasSurvey && (
          <MessageType
            href={props.createAppSurveyUrl}
            orientation="horizontal"
            icon="composer-inapp-survey"
            title="Survey"
            description="Create a survey that appears in your app when users meet certain conditions."
          />
        )}
        {props.hasScene && (
          <MessageType
            href={
              waffle.flag_is_active(WaffleFlag.ScenesContentImprovements)
                ? props.createAppExperienceUrl
                : props.createSceneUrl
            }
            orientation="horizontal"
            title="Scene"
            icon="composer-inapp-tour"
            description=" Create a multi-screen experience that appears in your app when users meet certain conditions."
          />
        )}
      </>
    )
  }

  function OptimizationMessageTypes(): JSX.Element {
    return (
      <>
        {props.hasExperimentsAccess && (
          <MessageType
            href={props.createExperimentUrl}
            orientation="horizontal"
            title="A/B Test"
            icon="composer-ab-test"
            description="Experiment with different variations of a message."
          />
        )}
      </>
    )
  }

  function ContentMessageTypes(): JSX.Element {
    return (
      <>
        {props.hasTemplates && (
          <MessageType
            href={props.createTemplatesUrl}
            orientation="horizontal"
            icon="composer-template"
            title="Template"
            description="Create reusable templates with personalized fields for each audience member."
          />
        )}
      </>
    )
  }

  function StandardMessageTypes(): JSX.Element {
    return (
      <div className="u-contain-spread-sm u-space-ends-6">
        <h1 className="u-text-center u-space-ends-6">
          What would you like to create?
        </h1>
        <div className="create-grid">
          <MessageType
            href={props.createMessageUrl}
            orientation="horizontal"
            icon="composer-message"
            title="Message"
            description="Send or schedule a message."
            data-selenium-target="composer-create-message"
          />

          {props.hasExperimentsAccess && (
            <MessageType
              href={props.createExperimentUrl}
              orientation="horizontal"
              icon="composer-ab-test"
              title="A/B Test"
              description="Experiment with different variations of a message."
            />
          )}
          {props.hasTriggersTag && (
            <MessageType
              href={props.createAutomatedUrl}
              orientation="horizontal"
              icon="composer-automation"
              title="Automation"
              description="Automatically send a message to users who meet certain conditions."
            />
          )}
          {props.hasJourneys && (
            <MessageType
              href={props.createJourneyModalOpenUrl}
              orientation="horizontal"
              icon="composer-series"
              title="Sequence"
              description="Automatically send a series of messages to users who meet certain conditions."
            />
          )}
          {props.hasInAppAutomation && (
            <MessageType
              href={props.createZoltarUrl}
              orientation="horizontal"
              icon="composer-inapp-automation"
              title="In-App Automation"
              description="Create a message that appears in your app when users meet certain conditions."
            />
          )}
          {props.hasSurvey && (
            <MessageType
              badge={
                <Badge
                  style="pill"
                  size="sm"
                  displayClasses="u-text-medium u-space-left-03 u-pad-sides-03"
                  foreground="white"
                  background="blue"
                >
                  NEW
                </Badge>
              }
              href={props.createAppSurveyUrl}
              orientation="horizontal"
              icon="composer-inapp-survey"
              title="Survey"
              description="Create a survey that appears in your app when users meet certain conditions."
            />
          )}
          {props.hasScene && (
            <MessageType
              badge={
                <Badge
                  style="pill"
                  size="sm"
                  displayClasses="u-text-medium u-space-left-03 u-pad-sides-03"
                  foreground="white"
                  background="blue"
                >
                  NEW
                </Badge>
              }
              href={
                waffle.flag_is_active(WaffleFlag.ScenesContentImprovements)
                  ? props.createAppExperienceUrl
                  : props.createSceneUrl
              }
              orientation="horizontal"
              icon="composer-inapp-tour"
              title="Scene"
              description=" Create a multi-screen experience that appears in your app when users meet certain conditions."
            />
          )}
          {props.hasTemplates && (
            <MessageType
              href={props.createTemplatesUrl}
              orientation="horizontal"
              icon="composer-template"
              title="Template"
              description="Create reusable templates with personalized fields for each audience member."
            />
          )}
          {props.hasAppleNews && (
            <MessageType
              href={props.createAppleNewsUrl}
              orientation="horizontal"
              icon="composer-applenews"
              title="Apple News"
              description="Send a push notification that links to an Apple News story."
            />
          )}
        </div>
      </div>
    )
  }

  function CreateNewButton(): JSX.Element {
    return (
      <a
        className={classnames('global-header__primary-action-link', {
          active: open,
        })}
        href="#"
        rel="create-popover"
        onClick={toggle}
        aria-label="create"
        data-selenium-target="composer-create-dropdown"
      >
        <SvgSprite name="add" />
      </a>
    )
  }
}

export default NewPushPopover
